import { useCallback, useEffect, useState } from 'react';

import { PaginationState } from '@tanstack/react-table';
import { useParams, useSearchParams } from 'react-router-dom';

import { DocDetailsPanel } from './components/doc-details-panel';
import { DocList } from './components/doc-list';
import { DocTableHeader } from './components/doc-table-header';
import { DownloadZipModal } from './components/download-zip';
import { DownloadFormat } from '../../../../../../../../../backend/src/types/types';
import { PageName, TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import { useCreateDocumentZip } from '@/api/mutations/useCreateDocumentZip';
import { useDownloadDocumentsList } from '@/api/mutations/useDownloadDocumentsList';
import { useDownloadDocumentZip } from '@/api/mutations/useDownloadDocumentZip';
import { useGetCaseDocThemes } from '@/api/queries/useGetCaseDocThemes';
import { useGetCaseDocTypes } from '@/api/queries/useGetCaseDocTypes';
import { useGetDocs } from '@/api/queries/useGetDocs';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetRelativityFilterOptions } from '@/api/queries/useGetRelativityFilterOptions';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { Doc } from '@/api/types/backend-dto-types';
import { useDocumentNaturalLanguageFilters } from '@/hooks/useNaturalLanguageFilters';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { DownloadTableModal } from '@/new/components/dialogs/download-dialog/download-table';
import { Icon } from '@/new/components/icon';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';
import { DocFilters } from '@/types';
import { buildDocumentFilterPills } from '@/utils/buildFilterPills';
import { removeFilterValue } from '@/utils/filterUtils';

export function Documents() {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;

  const [searchParams, setSearchParams] = useSearchParams();
  const processedParam = searchParams.get('processed');

  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: { label: 'Documents', path: 'docs' },
  });

  const [selectedDoc, setSelectedDoc] = useState<
    | (Doc & {
        hasKeyFacts: boolean;
        hasRelevantFacts: boolean;
        chunkMatchCount: number;
      })
    | undefined
  >();

  const [globalFilter, setGlobalFilter] = useState('');
  const [appliedGlobalFilter, setAppliedGlobalFilter] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });
  const [isProcessingNL, setIsProcessingNL] = useState(false);

  const [downloadModalIsOpen, setDownloadModalIsOpen] = useState(false);
  const [downloadZipModalIsOpen, setDownloadZipModalIsOpen] = useState(false);

  // Initialize filters from URL params if present
  const initialFilters: DocFilters = {};
  if (processedParam !== null) {
    initialFilters.processed = processedParam === 'true';
  }

  const [filters, setFilters] = useState<DocFilters>(initialFilters);
  const [sortBy, setSortBy] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  // Zip download state
  const [isCheckingExisting, setIsCheckingExisting] = useState(false);
  const [zipExists, setZipExists] = useState(false);
  const [zipInProgress, setZipInProgress] = useState(false);
  const [zipError, setZipError] = useState<string | undefined>();

  const handlePageSizeChange = (newPageSize: number) => {
    const newPaginationState = { pageIndex: 0, pageSize: newPageSize };
    setPagination(newPaginationState);
  };

  const { data: docTypes } = useGetCaseDocTypes({ caseId: subsetId });
  const { data: docThemes } = useGetCaseDocThemes({ caseId: subsetId });
  const { data: relativityFilterOptions } = useGetRelativityFilterOptions({ matterId });

  // Build a map of field name -> display name for Relativity filter pills
  const relativityFieldDisplayNames = relativityFilterOptions?.fields.reduce(
    (acc, field) => {
      acc[field.fieldName] = field.displayName;
      return acc;
    },
    {} as Record<string, string>,
  );
  const {
    data: responseDocs,
    isLoading: isLoadingDocs,
    refetch: refetchDocs,
  } = useGetDocs({
    caseId: subsetId,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    searchQuery: appliedGlobalFilter,
    processed: filters.processed,
    extensions: filters.extensions,
    startDate: filters.startDate,
    endDate: filters.endDate,
    documentTypes: filters.documentTypes,
    documentThemes: filters.documentThemes,
    hasKeyFacts: filters.hasKeyFacts,
    hasRelevantFacts: filters.hasRelevantFacts,
    sortBy,
    sortOrder,
    relativityFilters: filters.relativityFilters,
  });

  const { buildFiltersWithNL, isProcessing: isProcessingNLFiltersQuery } = useDocumentNaturalLanguageFilters({
    caseId: subsetId,
    onFiltersChange: (newFilters: DocFilters) => {
      setFilters(newFilters);
      setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
    },
    onSearchChange: (searchQuery: string) => {
      setAppliedGlobalFilter(searchQuery);
      setGlobalFilter(searchQuery);
    },
  });

  const onSearchCall = (searchValue?: string, useNaturalLanguage?: boolean) => {
    const value = searchValue ?? globalFilter;

    if (useNaturalLanguage && value.trim()) {
      setIsProcessingNL(true);

      buildFiltersWithNL(value);

      return;
    }

    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
    setAppliedGlobalFilter(value);

    // Automatically sort by chunk match count when searching
    if (globalFilter.trim() !== '') {
      setSortBy('chunkMatchCount');
      setSortOrder('DESC');
    }
  };

  useEffect(() => {
    if (!isProcessingNLFiltersQuery) {
      setIsProcessingNL(false);
    }
  }, [isProcessingNLFiltersQuery]);

  const clearSearch = async () => {
    setGlobalFilter('');
    setAppliedGlobalFilter('');
    setIsProcessingNL(false);

    // Clear chunk match count sort when clearing search
    if (sortBy === 'chunkMatchCount') {
      setSortBy('');
      setSortOrder('DESC');
    }
  };

  const handleRemoveFilter = (filterKey: string, value?: unknown) => {
    // Handle relativity filters by index
    if (filterKey === 'relativityFilters' && typeof value === 'number') {
      setFilters((prev) => {
        const newRelativityFilters = prev.relativityFilters?.filter((_, i) => i !== value);
        return {
          ...prev,
          relativityFilters: newRelativityFilters?.length ? newRelativityFilters : undefined,
        };
      });
      return;
    }

    setFilters((prev) => removeFilterValue(prev, filterKey as keyof DocFilters, value));
    // Clear the filter from url if it is there
    searchParams.delete(filterKey);
    setSearchParams(searchParams, { replace: true });
  };

  const handleClearAllFilters = () => {
    setFilters({});

    // Reset pagination to first page
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
  };

  useEffect(() => {
    refetchDocs();
  }, [pagination, appliedGlobalFilter, filters, refetchDocs]);

  // Pagination
  const goToPage = (val: number) => {
    const newPaginationState = { pageIndex: val, pageSize: pagination.pageSize };
    setPagination(newPaginationState);
  };
  const prevPage = () => goToPage(pagination.pageIndex - 1);
  const nextPage = () => goToPage(pagination.pageIndex + 1);

  const { fetchDownload, isLoadingDownload } = useDownloadDocumentsList({
    caseId: subsetId,
  });

  const { createZip } = useCreateDocumentZip({
    caseId: subsetId,
  });

  const { downloadZip } = useDownloadDocumentZip({
    caseId: subsetId,
    caseName: subset?.name || 'Documents',
  });

  const handleDownload = useCallback(
    ({ title, columns, downloadFormat }: { title: string; columns: string[]; downloadFormat: DownloadFormat }) => {
      trackCustomEvent(TrackingEventsV2.DownloadDocumentList, {
        downloadFormat,
        subsetId,
        isAllDocuments: subset?.isAllDocuments,
      });
      fetchDownload(
        {
          title,
          columns,
          format: downloadFormat,
          searchQuery: appliedGlobalFilter,
          processed: filters.processed ?? undefined,
          extensions: filters.extensions,
          startDate: filters.startDate ?? undefined,
          endDate: filters.endDate ?? undefined,
          documentTypes: filters.documentTypes,
          documentThemes: filters.documentThemes,
          hasKeyFacts: filters.hasKeyFacts ?? undefined,
          hasRelevantFacts: filters.hasRelevantFacts ?? undefined,
        },
        {
          onSuccess: async () => {
            await new Promise((resolve) => setTimeout(resolve, 200));
            setDownloadModalIsOpen(false);
          },
        },
      );
    },
    [subsetId, appliedGlobalFilter, filters, fetchDownload],
  );

  // Build filter object for zip creation
  const buildZipFilters = useCallback(() => {
    const hasFilters =
      appliedGlobalFilter ||
      filters.processed !== undefined ||
      (filters.extensions?.length ?? 0) > 0 ||
      filters.startDate ||
      filters.endDate ||
      (filters.documentTypes?.length ?? 0) > 0 ||
      (filters.documentThemes?.length ?? 0) > 0 ||
      filters.hasKeyFacts !== undefined ||
      filters.hasRelevantFacts !== undefined ||
      (filters.relativityFilters?.length ?? 0) > 0;

    if (!hasFilters) {
      return undefined;
    }

    return {
      searchQuery: appliedGlobalFilter || undefined,
      processed: filters.processed ?? undefined,
      extensions: filters.extensions,
      startDate: filters.startDate ?? undefined,
      endDate: filters.endDate ?? undefined,
      documentTypes: filters.documentTypes,
      documentThemes: filters.documentThemes,
      hasKeyFacts: filters.hasKeyFacts ?? undefined,
      hasRelevantFacts: filters.hasRelevantFacts ?? undefined,
      relativityFilters: filters.relativityFilters,
    };
  }, [appliedGlobalFilter, filters]);

  const handleCheckExisting = useCallback(() => {
    setIsCheckingExisting(true);
    setZipError(undefined);

    const zipFilters = buildZipFilters();

    createZip(zipFilters, {
      onSuccess: (response) => {
        setIsCheckingExisting(false);
        if (response.zipExists) {
          setZipExists(true);
          setZipInProgress(false);
        } else {
          // Job is active if already running OR if a new job was just created (jobId present)
          const jobIsActive = response.inProgress || !!response.jobId;
          setZipExists(false);
          setZipInProgress(jobIsActive);
        }
      },
      onError: (error) => {
        setIsCheckingExisting(false);
        setZipError(error instanceof Error ? error.message : 'Failed to check zip status');
      },
    });
  }, [createZip, buildZipFilters]);

  const handleDownloadZip = useCallback(() => {
    const zipFilters = buildZipFilters();

    downloadZip(zipFilters, {
      onSuccess: () => {
        setDownloadZipModalIsOpen(false);
        setZipExists(false);
      },
      onError: (error) => {
        setZipError(error instanceof Error ? error.message : 'Failed to download zip');
      },
    });
  }, [downloadZip, buildZipFilters]);

  // Handler for all documents zip - directly download without modal
  const handleOpenDownloadAllZipModal = useCallback(() => {
    createZip(undefined, {
      onSuccess: (response) => {
        // Download immediately if zip exists
        if (response.zipExists) {
          downloadZip(undefined, {
            onSuccess: () => {},
            onError: (error) => {
              console.error('Failed to download all documents zip:', error);
            },
          });
        } else {
          // If zip doesn't exist, show modal to track creation
          // Job is in progress if already running OR if a new job was just created (jobId present)
          const jobIsActive = response.inProgress || !!response.jobId;
          setZipExists(false);
          setZipInProgress(jobIsActive);
          setDownloadZipModalIsOpen(true);
        }
      },
      onError: (error) => {
        console.error('Failed to check all documents zip status:', error);
      },
    });
  }, [createZip, downloadZip]);

  useEffect(() => {
    if (responseDocs && responseDocs.docs && responseDocs.docs.length > 0) {
      setSelectedDoc(responseDocs.docs[0]);
    } else {
      setSelectedDoc(undefined);
    }
  }, [responseDocs]);

  useEffect(() => {
    trackPageView(PageName.Documents);
  }, []);

  // Keyboard navigation for documents
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!responseDocs?.docs || responseDocs.docs.length === 0) return;

      const currentIndex = selectedDoc ? responseDocs.docs.findIndex((doc) => doc.id === selectedDoc.id) : -1;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextIndex = currentIndex < responseDocs.docs.length - 1 ? currentIndex + 1 : currentIndex;
        if (nextIndex >= 0) {
          setSelectedDoc(responseDocs.docs[nextIndex]);
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : 0;
        setSelectedDoc(responseDocs.docs[prevIndex]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [responseDocs, selectedDoc]);

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb items={breadcrumbItems} />
      <div className="flex-1 overflow-hidden">
        <div className="flex h-full w-full flex-col text-sm">
          <DocTableHeader
            matterId={matterId}
            subsetId={subsetId}
            isAllDocuments={subset?.isAllDocuments}
            openDownloadModal={() => setDownloadModalIsOpen(true)}
            openDownloadZipModal={() => setDownloadZipModalIsOpen(true)}
            openDownloadAllZipModal={handleOpenDownloadAllZipModal}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            onSearchCall={onSearchCall}
            clearSearch={clearSearch}
            processingNL={isProcessingNL}
            goToPage={goToPage}
            currentPage={pagination.pageIndex}
            prevPage={prevPage}
            nextPage={nextPage}
            pageSize={pagination.pageSize}
            onPageSizeChange={handlePageSizeChange}
            hasActiveFilters={!!buildZipFilters()}
            totalPages={responseDocs?.pageCount}
            totalCount={responseDocs?.totalCount}
            totalUnfilteredCount={responseDocs?.totalUnfilteredCount}
            totalProcessedCount={responseDocs?.totalProcessedCount}
            filterPills={buildDocumentFilterPills(filters, handleRemoveFilter, relativityFieldDisplayNames)}
            onClearAllFilters={handleClearAllFilters}
            filters={filters}
            setFilters={setFilters}
            allDocumentTypes={docTypes ?? []}
            docThemes={docThemes ?? []}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
          />

          <div className="flex flex-1 flex-row divide-x overflow-y-scroll">
            <div className="flex max-w-[35%] min-w-[35%] flex-1 flex-col space-y-3 overflow-y-scroll p-3 px-3.5">
              <DocList
                docs={responseDocs?.docs}
                isLoadingDocs={isLoadingDocs}
                searchQuery={appliedGlobalFilter}
                selectedDoc={selectedDoc}
                setSelectedDoc={setSelectedDoc}
              />
            </div>
            <div className="flex grow flex-col">{selectedDoc && <DocDetailsPanel doc={selectedDoc} />}</div>
          </div>

          <Dialog open={downloadModalIsOpen} onOpenChange={setDownloadModalIsOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  <Icon className="lucide--download" />
                  Download Documents List
                </DialogTitle>
              </DialogHeader>
              <DownloadTableModal
                columnOptions={documentListDownloadOptions}
                downloadTitle={`${subset?.name} Documents`}
                loadingDownload={isLoadingDownload}
                totalCount={responseDocs?.totalCount}
                handleDownload={handleDownload}
              />
            </DialogContent>
          </Dialog>
          <Dialog open={downloadZipModalIsOpen} onOpenChange={setDownloadZipModalIsOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  <Icon className="lucide--file-archive" />
                  Download Documents Zip
                </DialogTitle>
              </DialogHeader>
              <DownloadZipModal
                caseId={subsetId}
                caseName={subset?.name || 'Documents'}
                onStartZipCreation={handleCheckExisting}
                onDownloadZip={handleDownloadZip}
                onCheckExisting={handleCheckExisting}
                isCheckingExisting={isCheckingExisting}
                zipExists={zipExists}
                zipInProgress={zipInProgress}
                zipError={zipError}
                hasActiveFilters={!!buildZipFilters()}
                totalDocuments={responseDocs?.totalCount}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}

const documentListDownloadOptions = [
  'ID',
  'Filename',
  'File Type',
  'Upload Date',
  'Author',
  'Date',
  'Document Type',
  'Summary',
  'Number Of Pages',
  'Number Of Facts',
  'Contains Key Facts',
  'Contains Relevant Facts',
  'Issues',
];
